/**
 * Read-only tools for the gateway AI assistant.
 *
 * Each tool is a thin wrapper around the existing api.js helper. Tools never
 * throw across the registry boundary — wrapApiError converts thrown api errors
 * into structured { ok: false, error } results so the model can self-correct.
 */

import * as api from "../../api";
import { assertPLCCapabilityManifest } from "../../plcCapabilityManifest";
import {
  connectorKindNames,
  publicCapabilityManifest,
  publicEdgeConnector,
  publicEdgeProgram,
  publicFieldbusReadiness,
} from "../plcPublicProjection";
import { publicSentence } from "../publicVocabulary";
import { findControllerByPublicName } from "../../pages/gateway/sections/plc-controllers-model";
import {
  inspectEdgeTags,
  normalizeEdgeTagCatalog,
} from "../../pages/gateway/sections/plc-edge-tag-catalog-model";

const wrap = (fn, code = "API_ERROR") => async (args) => {
  try {
    const data = await fn(args);
    return { ok: true, data };
  } catch (err) {
    return {
      ok: false,
      error: {
        code,
        message: String(err?.message || err) || "Request failed",
      },
    };
  }
};

const summarize = (rows, fields, max = 50) => {
  if (!Array.isArray(rows)) return rows;
  const trimmed = rows.slice(0, max).map((row) => {
    const out = {};
    for (const f of fields) if (row && f in row) out[f] = row[f];
    return out;
  });
  return { count: rows.length, truncated: rows.length > max, results: trimmed };
};

const redactPrivateTagIdentities = (value) => {
  if (Array.isArray(value)) return value.map(redactPrivateTagIdentities);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !["tag_id", "tagId", "TagID"].includes(key))
    .map(([key, entry]) => [key, redactPrivateTagIdentities(entry)]));
};

// ── Database connections ────────────────────────────────────────────

export const list_datasources = {
  name: "list_datasources",
  description:
    "List all configured database connections (datasources). Returns name, type, host, status, and the last connection `error` (if any). Use the `error` field to answer 'why is my X failing?' without needing to ping.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listDatasources();
    return summarize(rows, [
      "name",
      "type",
      "host",
      "port",
      "database",
      "status",
      "username",
      "ssl_mode",
      "error",
    ]);
  }),
};

export const describe_datasource = {
  name: "describe_datasource",
  description: "Get full details of one database connection by name.",
  parameters: {
    type: "object",
    properties: { name: { type: "string", minLength: 1 } },
    required: ["name"],
    additionalProperties: false,
  },
  execute: wrap(async ({ name }) => {
    const rows = await api.listDatasources();
    const found = (rows || []).find((r) => r.name === name);
    if (!found) {
      const err = new Error(`No datasource named "${name}".`);
      throw err;
    }
    return found;
  }),
};

export const ping_datasource = {
  name: "ping_datasource",
  description: "Ping a database connection to test reachability. Returns status.",
  parameters: {
    type: "object",
    properties: { name: { type: "string", minLength: 1 } },
    required: ["name"],
    additionalProperties: false,
  },
  execute: wrap(({ name }) => api.pingDatasource(name)),
};

export const list_db_schemas = {
  name: "list_db_schemas",
  description: "List schemas inside a database connection.",
  parameters: {
    type: "object",
    properties: { name: { type: "string", minLength: 1 } },
    required: ["name"],
    additionalProperties: false,
  },
  execute: wrap(async ({ name }) => {
    const rows = await api.listSchemas(name);
    return summarize(rows, ["name"], 100);
  }),
};

export const list_db_tables = {
  name: "list_db_tables",
  description: "List tables inside a schema of a database connection.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      schema: { type: "string", minLength: 1 },
    },
    required: ["name", "schema"],
    additionalProperties: false,
  },
  execute: wrap(async ({ name, schema }) => {
    const rows = await api.listTables(name, schema);
    return summarize(rows, ["name", "type"], 100);
  }),
};

export const list_db_columns = {
  name: "list_db_columns",
  description: "List columns of one table in a database connection.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      schema: { type: "string", minLength: 1 },
      table: { type: "string", minLength: 1 },
    },
    required: ["name", "schema", "table"],
    additionalProperties: false,
  },
  execute: wrap(async ({ name, schema, table }) => {
    const rows = await api.listColumns(name, schema, table);
    return summarize(rows, ["name", "type", "nullable"], 100);
  }),
};

export const get_supported_connector_types = {
  name: "get_supported_connector_types",
  description:
    "Return the list of database connector types this gateway supports, with their required fields and default ports. Use to know what to ask the user when proposing a new datasource.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: async () => ({
    ok: true,
    data: {
      types: [
        {
          type: "postgresql",
          label: "PostgreSQL",
          default_port: 5432,
          fields: ["host", "port", "database", "username", "password", "ssl_mode"],
          notes: "ssl_mode: 'disable' or 'require'.",
        },
        {
          type: "mysql",
          label: "MySQL",
          default_port: 3306,
          fields: ["host", "port", "database", "username", "password", "ssl_mode"],
          notes: "ssl_mode: 'disable' or 'require'.",
        },
        {
          type: "influxdb",
          label: "InfluxDB",
          default_port: 8086,
          fields: ["host (full URL)", "password (API token)", "options.org"],
          notes: "host should be a full URL like http://localhost:8086. Password is the API token.",
        },
        {
          type: "prometheus",
          label: "Prometheus",
          default_port: 9090,
          fields: ["host (full URL)", "password (bearer token, optional)"],
          notes: "host should be a full URL like http://localhost:9090. Bearer token only needed if behind an authenticating proxy.",
        },
      ],
    },
  }),
};

// ── API profiles (HTTP polling) ─────────────────────────────────────

export const list_api_profiles = {
  name: "list_api_profiles",
  description: "List configured HTTP API profiles (URL + polling settings).",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listApiProfiles();
    return summarize(rows, [
      "id",
      "name",
      "url",
      "method",
      "poll_interval_ms",
      "timeout_ms",
      "auth_mode",
      "has_auth_secret",
    ]);
  }),
};

export const list_profile_tags = {
  name: "list_profile_tags",
  description: "List tags that pull from a given API profile.",
  parameters: {
    type: "object",
    properties: { id: { type: "string", minLength: 1 } },
    required: ["id"],
    additionalProperties: false,
  },
  execute: wrap(async ({ id }) => {
    const rows = await api.listProfileTags(id);
    return summarize(rows, ["id", "name", "full_path", "value_path"], 50);
  }),
};

// ── Credentials ─────────────────────────────────────────────────────

export const list_credentials = {
  name: "list_credentials",
  description: "List flow credentials (used by HTTP action nodes for outbound auth).",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listCredentials();
    return summarize(rows, ["id", "name", "auth_mode", "provider"]);
  }),
};

export const test_credential = {
  name: "test_credential",
  description: "Test a credential's connection / token validity.",
  parameters: {
    type: "object",
    properties: { id: { type: "string", minLength: 1 } },
    required: ["id"],
    additionalProperties: false,
  },
  execute: wrap(({ id }) => api.testCredentialConnection(id)),
};

// ── OAuth apps ──────────────────────────────────────────────────────

export const list_oauth_providers = {
  name: "list_oauth_providers",
  description: "List the OAuth providers this gateway supports (slack, google, etc.).",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.listOAuthProviders),
};

export const list_oauth_apps = {
  name: "list_oauth_apps",
  description: "List OAuth apps configured in this gateway (provider, client_id only).",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listOAuthApps();
    return summarize(rows, ["provider", "client_id", "has_secret", "redirect_url"]);
  }),
};

export const get_oauth_app = {
  name: "get_oauth_app",
  description: "Get details for a single OAuth app by provider name.",
  parameters: {
    type: "object",
    properties: { provider: { type: "string", minLength: 1 } },
    required: ["provider"],
    additionalProperties: false,
  },
  execute: wrap(({ provider }) => api.getOAuthApp(provider)),
};

// ── MQTT brokers ────────────────────────────────────────────────────

export const list_mqtt_brokers = {
  name: "list_mqtt_brokers",
  description: "List configured MQTT brokers.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listMqttBrokers();
    return summarize(rows, ["id", "name", "host", "port", "tls", "client_id", "username"]);
  }),
};

export const get_mqtt_broker_state = {
  name: "get_mqtt_broker_state",
  description: "Get the current connection state of an MQTT broker.",
  parameters: {
    type: "object",
    properties: { id: { type: "string", minLength: 1 } },
    required: ["id"],
    additionalProperties: false,
  },
  execute: wrap(({ id }) => api.getMqttBrokerState(id)),
};

export const list_mqtt_subscriptions = {
  name: "list_mqtt_subscriptions",
  description: "List MQTT topic subscriptions on a broker.",
  parameters: {
    type: "object",
    properties: { brokerId: { type: "string", minLength: 1 } },
    required: ["brokerId"],
    additionalProperties: false,
  },
  execute: wrap(async ({ brokerId }) => {
    const rows = await api.listMqttSubscriptions(brokerId);
    return summarize(rows, ["id", "topic", "qos", "tag_path"]);
  }),
};

// ── Historian providers ─────────────────────────────────────────────

export const list_historian_providers = {
  name: "list_historian_providers",
  description:
    "List configured historian backends where tags store history. `default_local` is the built-in Control Seat historian. `live` = currently reachable.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listHistorianProviders();
    return summarize(rows, ["id", "name", "kind", "live", "has_password"]);
  }),
};

export const test_historian_provider = {
  name: "test_historian_provider",
  description:
    "Ping a historian provider without opening its live backend. Returns {ok, error?}.",
  parameters: {
    type: "object",
    properties: { id: { type: "string", minLength: 1 } },
    required: ["id"],
    additionalProperties: false,
  },
  execute: wrap(({ id }) => api.testHistorianProvider(id)),
};

// ── Users / groups / projects / workspace ───────────────────────────

export const list_users = {
  name: "list_users",
  description: "List users in this gateway. Excludes secrets / passwords.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listUsers();
    return summarize(rows, ["id", "username", "email", "role", "active"]);
  }),
};

export const list_groups = {
  name: "list_groups",
  description: "List user groups.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listGroups();
    return summarize(rows, ["id", "name", "description", "memberCount"]);
  }),
};

export const get_group = {
  name: "get_group",
  description: "Get details of one group, including members.",
  parameters: {
    type: "object",
    properties: { id: { type: "string", minLength: 1 } },
    required: ["id"],
    additionalProperties: false,
  },
  execute: wrap(({ id }) => api.getGroup(id)),
};

export const list_invites = {
  name: "list_invites",
  description:
    "List pending user invites — invites that have been sent but not yet redeemed. Returns id, email, role, invited_by (user id), created_at, expires_at. Use to answer 'do I have any pending invites?' or 'has Jane accepted her invite yet?'. Note: the one-time `invite_url` from `create_invite` is NOT returned here — to share the link again, revoke the existing invite and create a new one (`revoke_invite` then `create_invite`).",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const data = await api.listInvites();
    const rows = Array.isArray(data) ? data : data?.invites || [];
    return summarize(rows, [
      "id",
      "email",
      "role",
      "invited_by",
      "created_at",
      "expires_at",
    ]);
  }),
};

export const list_projects = {
  name: "list_projects",
  description: "List projects in this gateway.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listProjects();
    return summarize(rows, ["id", "name", "description"]);
  }),
};

// ── Tags ────────────────────────────────────────────────────────────

export const list_tags = {
  name: "list_tags",
  description:
    "List all tags this gateway has registered, **with full slash-separated paths** so you can resolve user-mentioned paths to tag IDs. Returns each tag's `tagId`, `name`, `fullPath` (e.g. `default/Floor1/Pump1/Pressure`), `path` (local segment), `type` (`number`/`boolean`/`string`/`bytes`/`json`), and `folder_id`. Use for: 'what tags do I have?'; finding the `tag_id` to pass to `propose_alarm` (alarms are per-tag — never invent a tag_id). Internally calls the folder-tree endpoint because the flat `/api/tags` endpoint returns only local paths, not full paths.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const tree = await api.getFolderTree();
    const browsers = Array.isArray(tree?.browsers) ? tree.browsers : [];
    const rows = [];
    for (const b of browsers) {
      for (const t of b.tags || []) {
        rows.push({
          tagId: t.tagId,
          name: t.name,
          fullPath: t.fullPath,
          path: t.path,
          type: t.type,
          folder_id: b.id,
        });
      }
    }
    return summarize(rows, ["tagId", "name", "fullPath", "path", "type", "folder_id"], 200);
  }),
};

// ── Custom connectors (collectors) ──────────────────────────────────

export const list_collectors = {
  name: "list_collectors",
  description:
    "List the workspace's custom connectors — the operator-authored Python scripts the gateway runs on a schedule (the `custom-collectors` page). Returns each collector's `id`, `name`, `description`, `enabled`, `poll_interval_ms`, `timeout_ms`, `env_keys`, `last_run_at`, `last_error` and `consecutive_failures`. Use to answer 'what collectors do I have?' and 'why is one failing?'. The script text is deliberately NOT returned: to read or change a script, the operator opens the collector's editor and you use `get_collector_script` / `set_collector_script`.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listCollectors();
    return summarize(Array.isArray(rows) ? rows : [], [
      "id",
      "name",
      "description",
      "enabled",
      "poll_interval_ms",
      "timeout_ms",
      "env_keys",
      "last_run_at",
      "last_success_at",
      "last_error",
      "consecutive_failures",
    ]);
  }),
};

// ── Alarms ──────────────────────────────────────────────────────────

export const list_alarms = {
  name: "list_alarms",
  description:
    "List configured alarm RULES (the `alarms-notifications` page) — each rule is a per-tag threshold definition. NOT the live alarm stream (that's on `alarms-management`). Returns `alarm_id` (the canonical id — pass this to `update_alarm`/`delete_alarm`/`acknowledge_alarm`), `tag_id`, `name`, `mode` (`above`/`below`/`between`/`outside_range`), `setpoint`, `setpoint_high`, `severity` (`critical`/`high`/`medium`/`low`), `message`, `on_delay_ms`, `off_delay_ms`, `enabled`, `notify_all`. Use to answer 'what alarms do I have?', 'find the pump pressure alarm', 'is the X alarm enabled?'.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const data = await api.listAlarms();
    const rows = Array.isArray(data) ? data : data?.alarms || [];
    return summarize(rows, [
      "alarm_id",
      "tag_id",
      "name",
      "mode",
      "setpoint",
      "setpoint_high",
      "severity",
      "message",
      "on_delay_ms",
      "off_delay_ms",
      "enabled",
      "notify_all",
    ]);
  }),
};

export const list_alarm_users = {
  name: "list_alarm_users",
  description:
    "List the USERS configured as alarm-notification recipients on the `alarms-notifications` page (this endpoint returns users only — group recipients are managed separately via `list_groups`). Returns `{users: [{user_id, username, first_name, last_name, email, avatar}]}`. Used to look up valid `notify_user_ids` when proposing a new alarm rule.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.listAlarmUsers),
};

export const get_me = {
  name: "get_me",
  description:
    "Get the currently logged-in user's profile (the user you're talking to). Returns id/user_id, username, email, role, permissions array, plus profile fields: first_name, last_name, phone, location, department, avatar. Use this to answer 'what's my email?', 'am I admin?', 'what's my username/role?', 'what's on my profile?'. The fields here are what the **Account Settings** page (`accounts-settings`) shows. If the user wants to CHANGE one of them, use `update_my_profile` (passwords are NOT routed through the AI — point users to the Account Settings → Password card).",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.getMe),
};

export const get_overview_stats = {
  name: "get_overview_stats",
  description:
    "Read the live system stats currently rendered on the **Platform Overview** page (section `platform-overview`): CPU usage %, Memory usage, Disk utilization, Uptime, plus System Info (date/time, timezone, system name) when present. Returns each stat as `{label, value, hint}` strings — exactly what the user sees on screen, including units and 'Waiting for data…' placeholders. Only works when the Overview page is currently mounted; if the user is elsewhere, navigate first or tell the user to open Overview. Use this for 'what's my CPU?', 'how much memory?', 'how long has the gateway been up?'. Does NOT return historical trend data.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: async () => {
    if (typeof document === "undefined") {
      return {
        ok: false,
        error: { code: "NO_WINDOW", message: "Cannot read overview in non-browser context." },
      };
    }
    const cards = Array.from(document.querySelectorAll("[data-gw-stat]"));
    if (cards.length === 0) {
      return {
        ok: false,
        error: {
          code: "NOT_ON_OVERVIEW",
          message:
            "The Platform Overview page is not currently mounted — the AI can only read these stats when the user is on the overview. Navigate to `platform-overview` first, or ask the user to open it.",
        },
      };
    }
    const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
    const stats = cards.map((card) => ({
      stat: card.getAttribute("data-gw-stat"),
      label: clean(card.querySelector(".gwStatLabel")?.textContent),
      value: clean(card.querySelector(".gwStatValue")?.textContent),
      hint: clean(card.querySelector(".gwStatHint")?.textContent),
    }));
    const systemInfo = {};
    const sysCard = document.querySelector('[data-gw-card="system_info"]');
    if (sysCard) {
      for (const field of sysCard.querySelectorAll(".gwField")) {
        const label = clean(field.querySelector(".gwLabel")?.textContent);
        const value = clean(field.querySelector(".gwHint")?.textContent);
        if (label) systemInfo[label] = value;
      }
    }
    return {
      ok: true,
      data: {
        stats,
        ...(Object.keys(systemInfo).length > 0 ? { systemInfo } : {}),
      },
    };
  },
};

export const get_workspace_settings = {
  name: "get_workspace_settings",
  description:
    "Get the gateway's workspace-wide settings (the **Gateway Settings** page, section id `gateway-settings`). Returns `{settings: {homepageRedirect, runtimeAccess, ...}}` — note the response is wrapped in a `settings` key. Defaults: `homepageRedirect=\"/editor\"`, `runtimeAccess=\"private\"`. Field meanings: `homepageRedirect` is the path the root URL `/` redirects to (e.g. `/editor` or `/<project>/<page>` for a custom landing page). `runtimeAccess` is `\"public\"` or `\"private\"` — whether runtime pages need login. Use this to answer 'where do users land?', 'what's the entry point URL?', 'is the runtime public?'. An empty `settings` object means the workspace is on defaults — that's not the same as 'feature missing'.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.getWorkspaceSettings),
};

export const list_ai_providers = {
  name: "list_ai_providers",
  description:
    "List configured AI/LLM providers, including the read-only Control Seat-managed provider. " +
    "Excludes API keys. Use before proposing a new provider so you don't duplicate one.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listAiProviders();
    return summarize(rows, [
      "id",
      "name",
      "endpoint",
      "default_model",
      "is_default",
      "provider_kind",
      "ready",
    ]);
  }),
};

export const get_ai_preference = {
  name: "get_ai_preference",
  description: "Get the currently selected AI provider/model preference.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.getAiPreference),
};

/**
 * Curated reference list of canonical model names per provider. Hard-coded
 * because the gateway can't query the upstream APIs for an authoritative
 * list at conversation time. The AI is told (in the system prompt) to use
 * only names returned here when filling a Default Model field — without
 * this, models hallucinate things like the literal string "2024".
 *
 * Update this list when new official model lines ship (or when the model
 * advertised in the gateway's defaults changes).
 */
const KNOWN_MODELS = {
  openai: [
    { name: "gpt-4o", note: "flagship; multimodal; balanced cost/quality" },
    { name: "gpt-4o-mini", note: "fast and cheap; gateway default" },
    { name: "o1-preview", note: "reasoning model; slower, costlier" },
    { name: "o1-mini", note: "smaller reasoning model" },
    { name: "o3-mini", note: "newer small reasoning model" },
  ],
  anthropic: [
    { name: "claude-sonnet-5", note: "current balanced Sonnet model" },
    { name: "claude-opus-4-8", note: "advanced Opus model for complex agentic coding" },
    { name: "claude-fable-5", note: "most capable generally available Claude model" },
    { name: "claude-haiku-4-5", note: "fast and cheap" },
  ],
  xai: [
    { name: "grok-4", note: "current Grok flagship" },
    { name: "grok-4-fast", note: "faster Grok variant" },
    { name: "grok-2", note: "previous-gen general-purpose" },
  ],
  google: [
    { name: "gemini-1.5-pro", note: "flagship; long context" },
    { name: "gemini-1.5-flash", note: "fast and cheap" },
    { name: "gemini-2.0-flash", note: "newer fast model" },
  ],
  ollama: [
    { name: "llama3", note: "Meta Llama 3 (your local install must have it)" },
    { name: "mistral", note: "Mistral 7B" },
    { name: "qwen2", note: "Alibaba Qwen 2" },
  ],
};

// ── On-prem licensing, TLS, and updates ─────────────────────────────
//
// These endpoints return 404 in cloud SaaS deployments — when that happens
// the wrap() helper surfaces an API_ERROR. The AI should recognize that
// and tell the user "this gateway is a cloud SaaS deployment, the License
// / TLS / Updates pages don't exist here." The Platform → License,
// Updates, and Web Server / TLS pages are on-prem only and hidden from
// the nav on cloud.

export const get_license = {
  name: "get_license",
  description:
    "Get the on-prem license state from the **Platform → License** page (`platform-license`). Returns `{mode, customer, install_id, gateway_install_id, edition, issued_at, expires_at, days_remaining, last_loaded_at, recent_audit?}`. `mode` is `licensed` / `expired` / `unlicensed`. `gateway_install_id` is the activation key the customer pastes when requesting a new license — it identifies THIS gateway, not the license. When `mode != \"licensed\"` runtime endpoints (live tag I/O, historian queries, runtime page rendering) return HTTP 402 — that's the answer to 'why isn't my data flowing?' if days_remaining is negative. **Returns 404 / API_ERROR on cloud SaaS** (no license file → licensing disabled). `recent_audit` only included for admins.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.fetchLicense),
};

export const get_system_info = {
  name: "get_system_info",
  description:
    "Get the public system info: build version, commit, build_time, plus a license summary (`mode`, `customer`, `expires_at`, `days_remaining`, `edition`). Always available (works on cloud SaaS too). Use to answer 'what version is this gateway running?' or 'when does the license expire?' without admin privileges.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.fetchSystemInfo),
};

export const get_tls_config = {
  name: "get_tls_config",
  description:
    "Get the on-prem HTTPS / TLS configuration from the **Platform → Web Server / TLS** page (`platform-tls`). Returns `{configured, https_listen, config?: {cert_subject, cert_dns_names[], cert_not_before, cert_not_after, cert_fingerprint, uploaded_at, uploaded_by}}`. `configured=false` means no certificate has been uploaded — HTTPS handshakes will fail until an admin uploads one. **Returns 404 / API_ERROR on cloud SaaS** (TLS terminated at the load balancer). On-prem only.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.fetchTLSConfig),
};

export const check_for_updates = {
  name: "check_for_updates",
  description:
    "Make a one-shot outbound call to controlseat.com to check whether a newer Control Seat release is available (the **Platform → Updates** page, `platform-updates`). Returns `{current, latest, update_needed, reachable, release_url?, download_url?, error?}`. `reachable=false` means the gateway couldn't contact controlseat.com — surface `error` in your reply rather than retrying. Admin-only (returns 403 for non-admins). The runtime never phones home on its own; this is the ONLY outbound call and it only fires when explicitly invoked.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.checkForUpdates),
};

export const list_known_models = {
  name: "list_known_models",
  description:
    "Return canonical model names for a given LLM provider so you don't " +
    "have to guess. Always call this before filling a 'Default Model' " +
    "field — the field is free-text, so anything you type is accepted, " +
    "but only canonical names actually work at the upstream API. " +
    "Hallucinating a year (\"2024\") or a partial name will look saved " +
    "but fail the moment a user makes a request. " +
    "Supported providers: openai, anthropic, xai, google, ollama. " +
    "For `custom` providers, ask the user — there's no canonical list.",
  parameters: {
    type: "object",
    properties: {
      provider: {
        type: "string",
        enum: ["openai", "anthropic", "xai", "google", "ollama"],
      },
    },
    required: ["provider"],
    additionalProperties: false,
  },
  execute: async ({ provider }) => {
    const list = KNOWN_MODELS[provider];
    if (!list) {
      return {
        ok: false,
        error: {
          code: "UNKNOWN_PROVIDER",
          message: `No canonical model list for "${provider}". Ask the user for a model name.`,
        },
      };
    }
    return { ok: true, data: { provider, models: list, count: list.length } };
  },
};

export const list_edge_runtimes = {
  name: "list_edge_runtimes",
  description:
    "List enrolled PLC edge runtimes. Returns each runtime's name, online/offline status, hostname, last_seen_at, deployed PLC summary, local digital-I/O status, bounded device performance (including processor temperature when the device reports it), and its data-retention policy when reported. Device performance is latest-heartbeat evidence; when offline it is last reported, not live. Retention policy never proves PLC execution or physical I/O. Call this before proposing local hardware I/O. " +
    "`gateway_id` is an internal handle for chaining tool calls only: pass it to other tools, and never print it or read it aloud — always identify a runtime to the user by its `name`.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listEdgeGateways();
    return {
      count: Array.isArray(rows) ? rows.length : 0,
      results: (Array.isArray(rows) ? rows : []).map((row) => ({
        gateway_id: row.id,
        name: row.name,
        status: row.status,
        hostname: row.hostname,
        last_seen_at: row.last_seen_at,
        runtime: row.runtime,
      })),
    };
  }),
};

export const get_edge_plc_programs = {
  name: "get_edge_plc_programs",
  description:
    "Read which PLC applications one enrolled edge runtime should be running and what it reports actually running. " +
    "Returns deployment state, versions, run state, and whether the runtime confirmed it is executing the exact " +
    "application that was sent. `gateway_id` is an internal handle for chaining tool calls — get it from " +
    "list_edge_runtimes, pass it here, and never show it to the user; refer to the runtime by its name. " +
    "Never infer that a device is executing something from the fact that we asked it to.",
  parameters: {
    type: "object",
    properties: { gateway_id: { type: "string", minLength: 1 } },
    required: ["gateway_id"],
    additionalProperties: false,
  },
  execute: wrap(async ({ gateway_id }) => {
    const inventory = await api.listEdgePLCPrograms(gateway_id);
    const programs = Array.isArray(inventory?.programs) ? inventory.programs : [];
    const stale = inventory?.snapshot_stale === true;
    return {
      gateway_id: inventory?.gateway_id,
      runtime: inventory?.gateway_name,
      connection: inventory?.connection,
      last_seen_at: inventory?.last_seen_at,
      // The freshness caveat is the whole reason this tool is trustworthy, so
      // it ships as a sentence the model can quote rather than a boolean it
      // has to remember to interpret.
      evidence: stale
        ? "This is the last report from the runtime, not a live reading. Say so before drawing conclusions."
        : "This is a current report from the runtime.",
      problem: publicSentence(inventory?.snapshot_error, ""),
      application_count: programs.length,
      applications: programs.slice(0, 100).map(publicEdgeProgram),
      truncated: programs.length > 100,
    };
  }, "PLC_EDGE_INVENTORY_UNAVAILABLE"),
};

export const list_edge_gpio_bindings = {
  name: "list_edge_gpio_bindings",
  description:
    "List GPIO-to-tag bindings for one enrolled edge runtime. Get gateway_id from list_edge_runtimes and pass it here; it is an internal handle, so never print it — name the runtime instead. Returns named tag paths, BCM pin numbers, direction, electrical settings, and safe state; tag IDs are intentionally never exposed.",
  parameters: {
    type: "object",
    properties: { gateway_id: { type: "string", minLength: 1 } },
    required: ["gateway_id"],
    additionalProperties: false,
  },
  // Whitelist fields so the "tag IDs are never exposed" promise in the
  // description is enforced here, not just assumed of the backend payload.
  execute: wrap(async ({ gateway_id }) => {
    const bindings = await api.listEdgeGPIOBindings(gateway_id);
    return (Array.isArray(bindings) ? bindings : []).map((b) => ({
      id: b.id,
      tag_name: b.tag_name,
      tag_path: b.tag_path,
      bcm_pin: b.bcm_pin,
      direction: b.direction,
      bias: b.bias,
      active_low: b.active_low,
      debounce_ms: b.debounce_ms,
      safe_state: b.safe_state,
    }));
  }),
};

export const get_edge_connectors = {
  name: "get_edge_connectors",
  description:
    "Read the I/O connections configured for one enrolled edge runtime and what the device reports about each. " +
    "Returns detected serial ports, GPIO board detection, and for every connection whether the device has confirmed it. " +
    "`gateway_id` is an internal handle for chaining tool calls — get it from list_edge_runtimes, pass it here, and " +
    "never show it to the user; refer to the runtime by its name. Sending a connection to a device never proves it is applied.",
  parameters: {
    type: "object",
    properties: { gateway_id: { type: "string", minLength: 1 } },
    required: ["gateway_id"],
    additionalProperties: false,
  },
	execute: wrap(async ({ gateway_id }) => {
    const result = await api.listEdgeConnectors(gateway_id);
    const connectors = Array.isArray(result?.connectors) ? result.connectors : [];
    const stale = result?.runtime?.snapshot_stale !== false;
	return {
	  gateway_id,
	  connection_setup: result?.configuration_available === true ? {
		available: true,
		guidance: "This workspace can review and apply device connections.",
	  } : {
		available: false,
		guidance: "I/O setup is unavailable for this workspace. Ask a workspace administrator to finish connection setup before creating tags or reviewing device settings.",
	  },
	  runtime: {
		connection: result?.runtime?.connection === "online" ? "online" : "offline",
		last_seen_at: result?.runtime?.last_seen_at,
		evidence: stale
		  ? "This is the last report from the runtime, not a live reading. Say so before drawing conclusions."
		  : "This is a current report from the runtime.",
		problem: publicSentence(result?.runtime?.snapshot_error, ""),
	  },
	  // Which connection kinds this device can actually run, by the names the
	  // wizard shows. The raw list is internal capability identifiers; a model
	  // that repeats those to an operator is quoting our build metadata at them.
	  supported_connection_kinds: connectorKindNames(result?.capabilities),
	  serial_ports: Array.isArray(result?.serial_ports) ? result.serial_ports.map(String) : [],
	  gpio_board: result?.gpio_board?.status === "detected" ? {
		status: "detected",
		label: result.gpio_board.profile_label || "Detected Raspberry Pi",
		model: result.gpio_board.detected_model || "",
		available_pins: (Array.isArray(result.gpio_board.available_pins) ? result.gpio_board.available_pins : []).map((pin) => ({
		  bcm: pin.bcm,
		  physical: pin.physical,
		  note: pin.note || "",
		})),
		// The caveat stays; the internal test-programme name goes.
		qualification: "Detected by software and matched to a known board. This is not proof that the wiring is correct.",
	  } : {
		status: result?.gpio_board?.status || "unavailable",
		label: "GPIO board not confirmed",
		available_pins: [],
	  },
      connection_count: connectors.length,
      connections: connectors.slice(0, 100).map((connector) => ({
        ...publicEdgeConnector(connector),
		public_configuration: connector.kind === "modbus_tcp_server" && connector.public_configuration ? {
		  listen_address: connector.public_configuration.listen_address,
		  unit_id: connector.public_configuration.unit_id,
		  max_clients: connector.public_configuration.max_clients,
		  request_timeout_ms: connector.public_configuration.request_timeout_ms,
		  idle_timeout_ms: connector.public_configuration.idle_timeout_ms,
		  source_cidrs: Array.isArray(connector.public_configuration.source_cidrs) ? connector.public_configuration.source_cidrs : [],
		} : undefined,
		public_mappings: connector.kind === "modbus_tcp_server" ? (Array.isArray(connector.public_mappings) ? connector.public_mappings : []).map((mapping) => ({
		  tag_path: mapping.tag_path,
		  table: mapping.table,
		  address: mapping.address,
		  value_type: mapping.value_type,
		  byte_order: mapping.byte_order,
		  word_order: mapping.word_order,
		  string_length: mapping.string_length,
		  string_raw: mapping.string_raw,
		  string_right_justify: mapping.string_right_justify,
		  flow: mapping.flow,
		  data_type: mapping.data_type,
		  tag_role: mapping.tag_role,
		})) : undefined,
        // Troubleshooting detail the operator can act on. State, point counts,
        // and the error line already come from publicEdgeConnector; this adds
        // only the numbers that help explain an intermittent connection.
        device_report: connector.applied ? {
          last_state_change: connector.applied.last_transition,
          last_successful_exchange: connector.applied.last_verified_exchange,
          reconnect_attempts: connector.applied.reconnect_attempts,
          queued_writes: connector.applied.queue_depth,
          board: publicSentence(connector.applied.board_profile_label, ""),
          host_check: publicSentence(connector.applied.host_validation, ""),
        } : null,
      })),
      truncated: connectors.length > 100,
    };
  }, "EDGE_CONNECTOR_INVENTORY_UNAVAILABLE"),
};

// Device templates are the answer to "connect another one of those": a model's
// complete register map, saved once. The point list is summarized rather than
// dumped — a template can carry fifty registers, and what the model needs to
// choose one is the device it describes, not every address in it.
export const list_device_templates = {
  name: "list_device_templates",
  description:
    "List the saved device templates: reusable register maps for a device model (a battery inverter, a meter), each " +
    "with its transport and all of its points. Use this whenever the user names a known device — \"the Solis\", " +
    "\"another one of those meters\" — before proposing any connection: a template already carries every point, so " +
    "apply_device_template connects it without re-authoring a single register. A template deliberately has no host, " +
    "port or device address; those belong to the one physical device and are what you ask the user for.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listDeviceTemplates();
    const templates = Array.isArray(rows) ? rows : [];
    return {
      count: templates.length,
      templates: templates.slice(0, 50).map((template) => {
        const points = Array.isArray(template?.points) ? template.points : [];
        const writable = points.filter((point) => point?.writable);
        return {
          // Both are accepted by apply_device_template; the name is what an
          // operator says out loud.
          template: template?.name,
          id: template?.id,
          vendor: template?.vendor || "",
          model: template?.model || "",
          connection_type: template?.kind === "modbus-rtu" ? "Modbus RTU (serial)" : "Modbus TCP",
          description: template?.description || "",
          point_count: points.length,
          writable_point_count: writable.length,
          // Enough to answer "does it read the state of charge?" without
          // shipping the whole register table into the conversation.
          reads: points.filter((point) => !point?.writable).slice(0, 40).map((point) => point?.name),
          writes: writable.slice(0, 20).map((point) => point?.name),
          suggested_tag_folder: template?.tag_folder_default || "",
          default_port: template?.settings?.default_port,
          shipped_with_control_seat: template?.source === "builtin",
        };
      }),
      guidance: templates.length
        ? "Connect one of these with the apply_device_template mutation: it needs the template, a name for this device, its host (or serial port), and its device address."
        : "No device templates are saved yet. Set the device up once with preview_edge_modbus_client, then an operator can save it as a template from I/O & connections.",
    };
  }),
};

export const get_runtime_hardware = {
  name: "get_runtime_hardware",
  description:
    "Read the bounded, user-facing Hardware & I/O inventory for one enrolled runtime. " +
    "Call list_edge_runtimes first and pass its gateway_id, an internal handle you must never print — name the runtime instead. Detection is read-only setup evidence: it never proves electrical behavior, device permissions, a configured connection, or working field I/O.",
  parameters: {
    type: "object",
    properties: { gateway_id: { type: "string", minLength: 1 } },
    required: ["gateway_id"],
    additionalProperties: false,
  },
  execute: wrap(async ({ gateway_id }) => {
    const result = await api.getEdgeRuntimeHardware(gateway_id);
    return {
      profile: result?.profile ? {
        name: result.profile.label,
        product: result.profile.product,
        summary: result.profile.summary,
        operating_system: result.profile.operating_system,
        io_summary: result.profile.io_summary,
        setup_mode: result.profile.setup_mode,
      } : null,
      status: result?.status || "unknown",
      summary: result?.summary || "",
      stale: result?.stale === true,
      reported_at: result?.reported_at,
      host: result?.host ? {
        operating_system: result.host.operating_system,
        architecture: result.host.architecture,
      } : null,
      resources: (Array.isArray(result?.resources) ? result.resources : []).slice(0, 32).map((resource) => ({
        name: resource?.label,
        detail: resource?.detail,
        status: resource?.status,
      })),
      terminal_groups: (Array.isArray(result?.terminal_groups) ? result.terminal_groups : []).slice(0, 32).map((group) => ({
        name: group?.label,
        detail: group?.detail,
        count: group?.count,
        status: group?.status,
      })),
      truncated: result?.truncated === true,
      guidance: "Use Hardware & I/O to review setup. Do not infer terminal wiring, electrical mode, calibration, direction, or safe output behavior from detection.",
    };
  }, "RUNTIME_HARDWARE_UNAVAILABLE"),
};

const publicEdgeTagInspection = (result) => ({
  path: result.path,
  eligible: result.eligible === true,
  code: result.code,
  message: result.message,
  data_type: result.data_type || result.entry?.dataType,
  writable: result.writable ?? result.entry?.writable,
  cloud_present: result.entry?.cloudPresent,
  cloud_data_type: result.entry?.cloudDataType || undefined,
  cloud_writable: result.entry?.cloudWritable,
});

export const inspect_edge_runtime_tags = {
  name: "inspect_edge_runtime_tags",
  description:
    "Read bounded, freshness-qualified tag availability for one exact enrolled edge runtime. " +
    "Use gateway_id from list_edge_runtimes as an internal handle only; never print it. Supply exact paths or a query, plus the required scalar data_type and writable access when selecting Modbus server mappings. " +
    "Complete evidence can prove a path absent. On a current runtime that supports it, exact inspection can resolve a path omitted by the bounded catalog through a backend-only live proof; otherwise it remains unreported. This tool never handles private identity material and never provisions, mirrors, writes, or infers a tag.",
  parameters: {
    type: "object",
    properties: {
      gateway_id: { type: "string", minLength: 1 },
      paths: { type: "array", maxItems: 100, uniqueItems: true, items: { type: "string", minLength: 1, maxLength: 2048 } },
      query: { type: "string", maxLength: 256 },
      data_type: { type: "string", enum: ["bool", "int64", "float64"] },
      writable: { type: "boolean" },
      limit: { type: "integer", minimum: 1, maximum: 100 },
    },
    required: ["gateway_id"],
    additionalProperties: false,
  },
  execute: wrap(async ({ gateway_id, paths = [], query = "", data_type, writable, limit = 50 }) => {
    const requestedPaths = paths.slice(0, 100);
    if (requestedPaths.length > 0) {
      const catalog = normalizeEdgeTagCatalog(await api.inspectEdgeTagCatalog(gateway_id, requestedPaths));
      const results = requestedPaths.map((path) =>
        inspectEdgeTags(catalog, { paths: [path], dataType: data_type, writable, limit: 1 })[0]
      );
      return {
        runtime: {
          connection: catalog.connection,
          last_seen_at: catalog.lastSeenAt || undefined,
          catalog_fresh: catalog.catalogFresh,
          snapshot_error: catalog.snapshotError || undefined,
        },
        catalog: {
          state: catalog.state,
          complete: catalog.complete,
          truncated: catalog.truncated,
          total_count: catalog.totalCount,
          returned_count: catalog.returnedCount,
          omitted_count: catalog.omittedCount,
          generated_at: catalog.generatedAt || undefined,
          truncation_reasons: catalog.truncationReasons,
        },
        results: results.map(publicEdgeTagInspection),
      };
    }
    const catalog = normalizeEdgeTagCatalog(await api.getEdgeTagCatalog(gateway_id, {
      query,
      limit: Math.min(100, Math.max(1, Number(limit) || 50)),
    }));
    return {
      runtime: {
        connection: catalog.connection,
        last_seen_at: catalog.lastSeenAt || undefined,
        catalog_fresh: catalog.catalogFresh,
        snapshot_error: catalog.snapshotError || undefined,
      },
      catalog: {
        state: catalog.state,
        complete: catalog.complete,
        truncated: catalog.truncated,
        total_count: catalog.totalCount,
        returned_count: catalog.returnedCount,
        omitted_count: catalog.omittedCount,
        generated_at: catalog.generatedAt || undefined,
        truncation_reasons: catalog.truncationReasons,
      },
      results: inspectEdgeTags(catalog, { query, dataType: data_type, writable, limit }).map(publicEdgeTagInspection),
    };
  }, "EDGE_TAG_CATALOG_UNAVAILABLE"),
};

export const list_plc_fleet_candidates = {
  name: "list_plc_fleet_candidates",
  description:
    "List published PLC programs and enrolled edge runtimes that can be proposed for a fleet rollout. " +
    "This is inventory only: online status does not prove the runtime has the signed baseline required by preview validation.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const [programResponse, gateways] = await Promise.all([
      api.listPLCProgramsForFleet(),
      api.listEdgeGateways(),
    ]);
    return {
      programs: (Array.isArray(programResponse?.programs) ? programResponse.programs : [])
        .filter((program) => program.published_version > 0)
        .map((program) => ({
          program_id: program.id,
          name: program.name,
          published_version: program.published_version,
          scan_interval_ms: program.scan_interval_ms,
        })),
      // install_id is a second internal handle with no use here — gateway_id
      // already chains every call this tool feeds.
      runtimes: (Array.isArray(gateways) ? gateways : []).map((gateway) => ({
        gateway_id: gateway.id,
        name: gateway.name,
        status: gateway.status,
        last_seen_at: gateway.last_seen_at,
      })),
    };
  }, "PLC_FLEET_CANDIDATES_UNAVAILABLE"),
};

export const get_plc_deployment_signing_policy = {
  name: "get_plc_deployment_signing_policy",
  description:
    "Read whether PLC deployment signing is set up on this environment, how long a signed package stays valid, and the last activation outcome. It never returns keys, key identifiers, secret references, preview tokens, or confirmation digests. This tool is read only; key creation and rotation remain human Gateway reviews and are blocked while any runtime is enrolled.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const status = await api.getPLCDeploymentSigningPolicy();
    const trusted = Array.isArray(status?.trusted_keys)
      ? status.trusted_keys.map((key) => key?.key_id).filter(Boolean)
      : [];
    return {
      mode: status?.mode || "unavailable",
      mutable: Boolean(status?.mutable),
      active: Boolean(status?.active),
      // Signing is either set up or it is not. Key identifiers are internal
      // handles: an operator cannot act on one, and quoting it in an answer
      // about whether a deployment is safe adds nothing they can use.
      signing_configured: Boolean(status?.active) && trusted.length > 0,
      trusted_key_count: trusted.length,
      package_validity_seconds: Number(status?.artifact_ttl_seconds || 0),
      source_metadata: {
        label: status?.source_metadata?.label || "",
        change_ticket: status?.source_metadata?.change_ticket || "",
      },
      last_activation: status?.last_activation ? {
        state: status.last_activation.state || "",
        message: status.last_activation.message || "",
        updated_at: status.last_activation.updated_at || "",
      } : null,
    };
  }),
};

export const list_plc_fleet_rollouts = {
  name: "list_plc_fleet_rollouts",
  description:
    "List durable PLC fleet rollout summaries: immutable plan digest, phase, state version, expiry, target count, and last update. " +
    "A paused draft is not confirmed and a queued/activated target is not healthy.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.listPLCFleetRollouts, "PLC_FLEET_ROLLOUTS_UNAVAILABLE"),
};

export const get_plc_fleet_rollout = {
  name: "get_plc_fleet_rollout",
  description:
    "Read one immutable PLC fleet plan and its current target state. Use this to explain stage gates, exact desired/rollback artifact identities, command progress, independent heartbeat timestamps, and failures. This tool cannot confirm, pause, cancel, or roll back anything.",
  parameters: {
    type: "object",
    properties: { rollout_id: { type: "string", minLength: 1 } },
    required: ["rollout_id"],
    additionalProperties: false,
  },
  execute: wrap(async ({ rollout_id }) => {
    const record = await api.getPLCFleetRollout(rollout_id);
    return { id: record?.id, plan: record?.plan, state: record?.state };
  }, "PLC_FLEET_ROLLOUT_UNAVAILABLE"),
};

export const get_plc_fleet_rollout_events = {
  name: "get_plc_fleet_rollout_events",
  description:
    "Read the redacted audit timeline for one PLC fleet rollout. Returns state-versioned events and safe diagnostics; actor identities and signed package bytes are intentionally not exposed.",
  parameters: {
    type: "object",
    properties: { rollout_id: { type: "string", minLength: 1 } },
    required: ["rollout_id"],
    additionalProperties: false,
  },
  execute: wrap(async ({ rollout_id }) => {
    // The relay returns up to 500 events, each with a details blob; keep the
    // latest 100 in model context and report what was dropped.
    const events = await api.listPLCFleetRolloutEvents(rollout_id);
    const rows = Array.isArray(events) ? events : [];
    return { count: rows.length, truncated: rows.length > 100, events: rows.slice(-100) };
  }, "PLC_FLEET_AUDIT_UNAVAILABLE"),
};

// ── PLC capability truth and roadmap ───────────────────────────────

export const get_plc_capabilities = {
  name: "get_plc_capabilities",
  description:
    "Read which PLC features this host can actually run. Use this before claiming that a language, protocol, " +
    "runtime, simulation, deployment, fleet, source-control, or hardware feature works. `executable` true means " +
    "the feature runs here; anything else means it does not, however much the configuration screens let you save. " +
    "Report the stated limitations rather than inventing a workaround. `scope` describes the host serving this " +
    "request, never an enrolled remote Pi or IPC. Describe features by name in plain language; the internal " +
    "capability identifiers behind them are deliberately not returned and must not be guessed at.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: async () => {
    try {
      const manifest = await api.getPlcCapabilities();
      // Validate against the full manifest, then project. Validation must see
      // the real document; the model must not.
      const validated = assertPLCCapabilityManifest(manifest, { allowLegacy: false });
      return { ok: true, data: publicCapabilityManifest(validated) };
    } catch (err) {
      return {
        ok: false,
        error: {
          code: "PLC_CAPABILITIES_UNAVAILABLE",
          message: String(err?.message || err) || "Capability manifest unavailable",
          hint: "Treat PLC feature support as unverified. Do not infer support from prompt text, visible configuration, host prerequisites, or earlier responses.",
        },
      };
    }
  },
};

const publicPLCControllerForAI = (controller) => ({
  name: controller?.name,
  description: controller?.description,
  mode: controller?.mode,
  desired_state: controller?.desired_state,
  desired_revision: controller?.desired_revision,
  desired_generation: controller?.desired_generation,
  managed_legacy: controller?.managed_legacy === true,
  configuration_only: controller?.configuration_only === true,
  assignment_enforced: controller?.assignment_enforced === true,
  target: {
    name: controller?.target?.name,
    kind: controller?.target?.kind,
    connectivity: controller?.target?.connectivity,
    platform: controller?.target?.platform,
    architecture: controller?.target?.architecture,
    execution_model: controller?.target?.execution_model,
  },
  applications: (Array.isArray(controller?.programs) ? controller.programs : []).map((program) => ({
    name: program?.name,
    enabled: program?.enabled === true,
    published_version: program?.published_version,
    order: program?.order,
  })),
  connectors: (Array.isArray(controller?.connectors) ? controller.connectors : []).map((connector) => ({
    name: connector?.name,
    kind: connector?.kind,
    order: connector?.order,
  })),
  policy: controller?.policy,
  actual: controller?.actual,
  capability: controller?.capability,
  lifecycle: {
    review_required: controller?.lifecycle?.review_required === true,
    message: controller?.lifecycle?.message,
    actions: Object.fromEntries(["start", "stop", "restart", "reset", "retry"].map((action) => [action, {
      available: controller?.lifecycle?.actions?.[action]?.available === true,
      message: controller?.lifecycle?.actions?.[action]?.message,
    }])),
  },
  warnings: Array.isArray(controller?.warnings) ? controller.warnings : [],
  updated_at: controller?.updated_at,
});

export const list_plc_controllers = {
  name: "list_plc_controllers",
  description:
    "List virtual PLC controllers by public controller and runtime name. Returns desired versus authenticated actual state, evidence freshness, isolation truth, authoritative per-action lifecycle availability, public application/connector assignments, and bounded resource-policy summaries. Internal controller, application, connector, tag, session, and tenant IDs are removed. A configuration-only row is not a worker and does not prove isolation or execution.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const payload = await api.listPLCControllers();
    const controllers = Array.isArray(payload?.controllers) ? payload.controllers : [];
    return {
      count: controllers.length,
      capability: payload?.capability,
      controllers: controllers.slice(0, 100).map(publicPLCControllerForAI),
      truncated: controllers.length > 100,
    };
  }, "PLC_CONTROLLERS_UNAVAILABLE"),
};

export const get_plc_controller_events = {
  name: "get_plc_controller_events",
  description:
    "Read the bounded audit history for one controller selected by exact public controller name and, when names repeat, exact public runtime name. This tool resolves opaque identities internally and never returns them. It is read only and cannot start, stop, restart, migrate, delete, assign, or reconfigure a controller.",
  parameters: {
    type: "object",
    properties: {
      controller_name: { type: "string", minLength: 1 },
      target_name: { type: "string", description: "Exact public runtime name; required when the controller name is ambiguous." },
      limit: { type: "integer", minimum: 1, maximum: 100 },
    },
    required: ["controller_name"],
    additionalProperties: false,
  },
  execute: async ({ controller_name, target_name = "", limit = 50 }) => {
    try {
      const payload = await api.listPLCControllers();
      const controllers = Array.isArray(payload?.controllers) ? payload.controllers : [];
      const controller = findControllerByPublicName(controllers, controller_name, target_name);
      if (!controller) {
        const sameName = controllers.filter((candidate) =>
          String(candidate?.name || "").trim().toLocaleLowerCase() === String(controller_name || "").trim().toLocaleLowerCase()
        );
        if (sameName.length > 1 && !String(target_name || "").trim()) {
          return {
            ok: false,
            error: {
              code: "PLC_CONTROLLER_AMBIGUOUS",
              message: `More than one controller is named ${controller_name}. Supply one of these public runtime names: ${sameName.map((candidate) => candidate?.target?.name).filter(Boolean).join(", ")}.`,
            },
          };
        }
        return { ok: false, error: { code: "PLC_CONTROLLER_NOT_FOUND", message: "No controller matches that public controller and runtime name." } };
      }
      const bounded = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 50)));
      const eventPayload = await api.listPLCControllerEvents(controller.id, bounded);
      const events = Array.isArray(eventPayload?.events) ? eventPayload.events : [];
      return {
        ok: true,
        data: {
          controller_name: controller.name,
          target_name: controller.target?.name,
          count: events.length,
          events: events.slice(0, bounded).map((event) => ({
            kind: event?.kind,
            actor_name: event?.actor_name,
            message: event?.message,
            occurred_at: event?.occurred_at,
          })),
        },
      };
    } catch (err) {
      return { ok: false, error: { code: "PLC_CONTROLLER_EVENTS_UNAVAILABLE", message: String(err?.message || err) || "Controller history unavailable" } };
    }
  },
};

const publicOutputRetryForAI = (binding) => {
  const state = String(binding?.delivery_state || "");
  if (state === "accepted") {
    return {
      classification: "none",
      message: "The output writer accepted this desired output; no retry is pending.",
    };
  }
  if (state === "leased") {
    return { classification: "in_progress", message: "Writer delivery is currently in progress." };
  }
  if (state === "pending" && binding?.retry?.classification === "retry_scheduled") {
    return {
      classification: "retry_scheduled",
      message: "Writer acceptance has not been recorded; delivery will retry.",
    };
  }
  if (state === "pending") {
    return { classification: "queued", message: "The durable desired output is queued for writer delivery." };
  }
  return { classification: "unavailable", message: "Writer delivery status is unavailable." };
};

const publicOutputDeliveryForAI = (payload, limit) => {
  const available = payload?.available === true;
  const total = Number(payload?.counts?.total || 0);
  const accepted = Number(payload?.counts?.accepted || 0);
  return {
    available,
    message: available
      ? total > 0
        ? `${total} desired outputs are durable; the output writer has accepted ${accepted}. Device observation is unavailable from this delivery path.`
        : "No durable desired physical outputs are recorded for the current running controller generation."
      : "Physical-output delivery status is unavailable.",
    counts: {
      total,
      pending: Number(payload?.counts?.pending || 0),
      leased: Number(payload?.counts?.leased || 0),
      accepted,
      shown: Number(payload?.counts?.shown || 0),
      truncated: payload?.counts?.truncated === true,
    },
    stages: {
      desired_durable: payload?.stages?.desired_durable === true,
      writer_accepted: payload?.stages?.writer_accepted === true,
      device_observed_available: false,
      device_observed: false,
    },
    bindings: (Array.isArray(payload?.bindings) ? payload.bindings : []).slice(0, limit).map((binding) => ({
      full_path: String(binding?.full_path || ""),
      data_type: String(binding?.data_type || ""),
      source_time: binding?.source_time || null,
      delivery_state: String(binding?.delivery_state || ""),
      attempts: Number(binding?.attempts || 0),
      accepted_at: binding?.accepted_at || null,
      retry: publicOutputRetryForAI(binding),
      stages: {
        desired_durable: binding?.stages?.desired_durable === true,
        writer_accepted: binding?.stages?.writer_accepted === true,
        device_observed_available: false,
        device_observed: false,
      },
    })),
  };
};

export const get_plc_controller_output_delivery = {
  name: "get_plc_controller_output_delivery",
  description:
    "Read bounded, redacted physical-output delivery status for one controller selected by exact public controller name and, when needed, public runtime name. This is read only. Desired means durable intent; writer accepted means only that the ordinary output writer accepted the request; device observation is always unavailable on this path. It never returns desired values, raw writer errors, lease/evidence identities, tag IDs, controller IDs, or runtime IDs, and cannot write or retry an output.",
  parameters: {
    type: "object",
    properties: {
      controller_name: { type: "string", minLength: 1 },
      target_name: { type: "string", description: "Exact public runtime name; required when the controller name is ambiguous." },
      limit: { type: "integer", minimum: 1, maximum: 200 },
    },
    required: ["controller_name"],
    additionalProperties: false,
  },
  execute: async ({ controller_name, target_name = "", limit = 50 }) => {
    try {
      const payload = await api.listPLCControllers();
      const controllers = Array.isArray(payload?.controllers) ? payload.controllers : [];
      const controller = findControllerByPublicName(controllers, controller_name, target_name);
      if (!controller) {
        const sameName = controllers.filter((candidate) =>
          String(candidate?.name || "").trim().toLocaleLowerCase() === String(controller_name || "").trim().toLocaleLowerCase()
        );
        if (sameName.length > 1 && !String(target_name || "").trim()) {
          return {
            ok: false,
            error: {
              code: "PLC_CONTROLLER_AMBIGUOUS",
              message: `More than one controller is named ${controller_name}. Supply one of these public runtime names: ${sameName.map((candidate) => candidate?.target?.name).filter(Boolean).join(", ")}.`,
            },
          };
        }
        return { ok: false, error: { code: "PLC_CONTROLLER_NOT_FOUND", message: "No controller matches that public controller and runtime name." } };
      }
      const bounded = Math.max(1, Math.min(200, Math.trunc(Number(limit) || 50)));
      const delivery = await api.getPLCControllerOutputDelivery(controller.id, bounded);
      return {
        ok: true,
        data: {
          controller_name: controller.name,
          target_name: controller.target?.name,
          ...publicOutputDeliveryForAI(delivery, bounded),
        },
      };
    } catch (err) {
      return { ok: false, error: { code: "PLC_CONTROLLER_OUTPUT_DELIVERY_UNAVAILABLE", message: "Physical-output delivery status is unavailable." } };
    }
  },
};

const publicHostSupportForAI = (record) => ({
  available: record?.available === true,
  authenticated: record?.authenticated === true,
  cached: record?.cached === true,
  activation_eligible: record?.activation_eligible === true,
  activation_code: record?.activation_code,
  activation_message: record?.activation_message,
  source: record?.source,
  received_at: record?.received_at,
  snapshot: record?.available === true ? {
    profile: record?.snapshot?.profile,
    target_kind: record?.snapshot?.target_kind,
    runtime_profile: record?.snapshot?.runtime_profile,
    policy_sha256: record?.snapshot?.policy_sha256,
    evidence: {
      profile: record?.snapshot?.evidence?.profile,
      runtime_profile: record?.snapshot?.evidence?.runtime_profile,
      observed_at: record?.snapshot?.evidence?.observed_at,
      platform: record?.snapshot?.evidence?.platform,
      architecture: record?.snapshot?.evidence?.architecture,
      systemd_version: record?.snapshot?.evidence?.systemd_version,
      checks: (Array.isArray(record?.snapshot?.evidence?.checks) ? record.snapshot.evidence.checks : []).map((check) => ({
        name: check?.name,
        passed: check?.passed === true,
        diagnostic: check?.passed === true ? undefined : "Required primitive was not proven.",
      })),
    },
    capacity: {
      cpu_millicores: record?.snapshot?.capacity?.cpu_millicores,
      memory_bytes: record?.snapshot?.capacity?.memory_bytes,
      tasks: record?.snapshot?.capacity?.tasks,
      open_files: record?.snapshot?.capacity?.open_files,
      read_bytes_per_second: record?.snapshot?.capacity?.read_bytes_per_second,
      write_bytes_per_second: record?.snapshot?.capacity?.write_bytes_per_second,
      read_iops: record?.snapshot?.capacity?.read_iops,
      write_iops: record?.snapshot?.capacity?.write_iops,
    },
    result: {
      profile: record?.snapshot?.result?.profile,
      status: record?.snapshot?.result?.status,
      stale: record?.snapshot?.result?.stale === true,
      code: record?.snapshot?.result?.code,
      message: record?.snapshot?.result?.message,
      observed_at: record?.snapshot?.result?.observed_at,
      age_ms: record?.snapshot?.result?.age_ms,
    },
    issued_at: record?.snapshot?.issued_at,
    expires_at: record?.snapshot?.expires_at,
  } : undefined,
});

export const get_plc_controller_support = {
  name: "get_plc_controller_support",
  description:
    "Read cached authenticated Linux host-isolation support evidence and bounded history for one exact public runtime name. This is read only: it cannot rerun a probe, start/stop/restart a controller, deploy an application, or change plant I/O. `activation_eligible` means only that fresh evidence may enter activation preflight; it does not mean the planned isolated-controller capability is executable. Treat unavailable or stale evidence literally. Opaque runtime/install/tenant/boot/cgroup/signing/package/tag identities and raw diagnostics are removed.",
  parameters: {
    type: "object",
    properties: {
      runtime_name: { type: "string", minLength: 1, description: "Exact public runtime name." },
      runtime_kind: { type: "string", enum: ["local", "edge"], description: "Use when public names overlap." },
      limit: { type: "integer", minimum: 1, maximum: 100 },
    },
    required: ["runtime_name"],
    additionalProperties: false,
  },
  execute: async ({ runtime_name, runtime_kind = "", limit = 20 }) => {
    try {
      const payload = await api.listPLCControllerRuntimes();
      const wantedName = String(runtime_name || "").trim().toLocaleLowerCase();
      const wantedKind = String(runtime_kind || "").trim().toLocaleLowerCase();
      const runtimes = (Array.isArray(payload?.runtimes) ? payload.runtimes : []).filter((runtime) => (
        String(runtime?.name || "").trim().toLocaleLowerCase() === wantedName &&
        (!wantedKind || String(runtime?.kind || "").trim().toLocaleLowerCase() === wantedKind)
      ));
      if (runtimes.length === 0) {
        return { ok: false, error: { code: "PLC_CONTROLLER_RUNTIME_NOT_FOUND", message: "No runtime matches that public name and kind." } };
      }
      if (runtimes.length > 1) {
        return {
          ok: false,
          error: {
            code: "PLC_CONTROLLER_RUNTIME_AMBIGUOUS",
            message: "More than one runtime has that public name. Specify runtime_kind or rename duplicate runtime rows before AI inspection.",
          },
        };
      }
      const runtime = runtimes[0];
      const bounded = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 20)));
      const support = await api.getPLCControllerRuntimeSupport(runtime.kind, runtime.id, bounded);
      const history = Array.isArray(support?.history) ? support.history : [];
      return {
        ok: true,
        data: {
          runtime: { name: runtime.name, kind: runtime.kind },
          latest: publicHostSupportForAI(support?.latest),
          history: history.slice(0, bounded).map(publicHostSupportForAI),
          count: Math.min(history.length, bounded),
          limit: bounded,
        },
      };
    } catch (err) {
      return { ok: false, error: { code: "PLC_CONTROLLER_SUPPORT_UNAVAILABLE", message: String(err?.message || err) || "Controller support evidence unavailable" } };
    }
  },
};

export const get_plc_fieldbus_readiness = {
  name: "get_plc_fieldbus_readiness",
  description:
    "Check whether EtherCAT, PROFINET, PROFIBUS, or EtherNet/IP cyclic I/O can actually run on the host serving this request. " +
    "Treat a protocol as usable only when its `available` field is exactly true. A detected network interface, a serial port, " +
    "a saved configuration, or an earlier answer never proves a protocol works. " +
    "When a protocol reports unavailable, tell the user it is unavailable and stop there — do not speculate about why, " +
    "do not suggest workarounds, and do not describe Control Seat's internal readiness process.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  // Projected, not passed through. The raw probe manifest is our release-gate
  // register: it names unreleased backends and the exact evidence we have not
  // produced for them. See plcPublicProjection.js.
  execute: wrap(async () => publicFieldbusReadiness(await api.getPlcFieldbusProbes())),
};

export const get_modbus_runtime_status = {
  name: "get_modbus_runtime_status",
  description:
    "Read whether this gateway target can actually run Modbus I/O, or can only store the configuration, and why. Check this before proposing a TCP/RTU connection or troubleshooting why one never comes online.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const status = await api.getModbusRuntimeStatus();
    // `mode: "configuration_only"` is a wire enum; the reason underneath it is
    // already written for an operator, so it passes through as-is.
    return {
      can_run_modbus_here: status?.available === true,
      can_bind_tags_here: status?.binding_available === true,
      explanation: publicSentence(
        status?.reason,
        status?.available === true
          ? "This host can run Modbus connections."
          : "This host stores Modbus configuration but cannot run it. Deploy the connection to a runtime that can."
      ),
    };
  }),
};

export const list_modbus_devices = {
  name: "list_modbus_devices",
  description:
    "List configured Modbus TCP and RTU client/master devices without secrets. Returns transport, endpoint/serial settings, unit id, enabled state, and operator-assigned name. Tag register/coil mappings remain normal tag sources in the editor.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listModbusDevices();
    return summarize(rows, [
      "id",
      "name",
      "transport",
      "host",
      "port",
      "serial_port",
      "baud_rate",
      "data_bits",
      "parity",
      "stop_bits",
      "unit_id",
      "timeout_ms",
      "poll_interval_ms",
      "enabled",
      "status",
      "error",
    ]);
  }),
};

export const list_modbus_serial_ports = {
  name: "list_modbus_serial_ports",
  description:
    "List serial ports visible to this runtime host for Modbus RTU configuration. An empty list on a cloud control plane is expected and does not imply that an edge runtime lacks ports.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.listModbusSerialPorts),
};

export const get_modbus_server_config = {
  name: "get_modbus_server_config",
  description:
    "Read the saved native Modbus TCP server configuration: enabled state, exact listen address, unit id, connection/request bounds, and all four-table mappings by portable tag path. This is the inbound server/slave role, not a Modbus client device. The tool is read only and does not enable, save, or restart the listener. A failed read on a cloud target means the local protocol runtime is unavailable; do not imply that the server is running remotely.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    // The mapping table is unbounded (the whole 4×65536 address space is
    // legal); cap what enters model context and say what was dropped.
    const config = await api.getModbusServerConfig();
    const mappings = Array.isArray(config?.mappings) ? config.mappings : [];
    return {
      ...config,
      mapping_count: mappings.length,
      mappings_truncated: mappings.length > 50,
      mappings: mappings.slice(0, 50),
    };
  }, "MODBUS_SERVER_CONFIG_UNAVAILABLE"),
};

export const get_modbus_server_status = {
  name: "get_modbus_server_status",
  description:
    "Read live native Modbus TCP server availability, enabled/running state, bound listener, unit id, mapping count, last error, start time, connected-client counts, requests, protocol exceptions, and transport errors. Check available and running explicitly; saved configuration alone is never proof of an active listener.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(api.getModbusServerStatus, "MODBUS_SERVER_STATUS_UNAVAILABLE"),
};

export const get_opcua_server_role_config = {
  name: "get_opcua_server_role_config",
  description:
    "Read the public disabled OPC UA server desired revision: endpoint, namespace URI, source-network allowlist, strict security/identity mode, configured-readiness booleans for certificate/key/trust material, resource bounds, and normal-tag mappings by public NodeId and portable full path. Opaque security reference names are configure-only and never enter this tool. This is not an OPC UA client connection, and Desired is never proof of Applied or Listening.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    // Up to 4096 mappings are legal; cap what enters model context.
    const config = await api.getOpcuaServerRoleConfig();
    const mappings = Array.isArray(config?.mappings) ? config.mappings : [];
    return {
      revision: config?.revision,
      enabled: config?.enabled === true,
      listen_address: config?.listen_address,
      namespace_uri: config?.namespace_uri,
      source_cidrs: Array.isArray(config?.source_cidrs) ? config.source_cidrs.slice(0, 64) : [],
      security_mode: config?.security_mode,
      security_policy: config?.security_policy,
      anonymous_enabled: config?.anonymous_enabled === true,
      identity_mode: config?.identity_mode,
      identity_provider: config?.identity_provider,
      server_certificate_configured: config?.server_certificate_configured === true,
      server_private_key_configured: config?.server_private_key_configured === true,
      application_trust_list_configured: config?.application_trust_list_configured === true,
      application_issuer_list_configured: config?.application_issuer_list_configured === true,
      application_crl_configured: config?.application_crl_configured === true,
      x509_user_trust_list_configured: config?.x509_user_trust_list_configured === true,
      x509_user_issuer_list_configured: config?.x509_user_issuer_list_configured === true,
      x509_user_crl_configured: config?.x509_user_crl_configured === true,
      max_channels: config?.max_channels,
      max_sessions: config?.max_sessions,
      max_subscriptions_per_session: config?.max_subscriptions_per_session,
      max_monitored_items_per_subscription: config?.max_monitored_items_per_subscription,
      max_nodes_per_read: config?.max_nodes_per_read,
      max_nodes_per_write: config?.max_nodes_per_write,
      mapping_count: mappings.length,
      mappings_truncated: mappings.length > 50,
      mappings: mappings.slice(0, 50).map((mapping) => ({
        node_id: mapping?.node_id,
        tag_path: mapping?.tag_path,
        access: mapping?.access,
        data_type: mapping?.data_type,
      })),
    };
  }, "OPCUA_SERVER_ROLE_CONFIG_UNAVAILABLE"),
};

export const get_opcua_server_role_status = {
  name: "get_opcua_server_role_status",
  description:
    "Read canonical desired/applied/listening truth for the native OPC UA server role: desired and applied revision numbers, desired_equals_applied, runtime_matches_applied, reconciled, listener_closed_applied, executable/running flags, blocker, security warnings, dependency facts, and bounded internal read projection. Public status deliberately omits configuration digests because they cover private references. Current composition advances Desired only: Applied remains absent, all reconciliation flags remain false, and the independently closed configuration-only listener is not an applied revision. Never infer network browse/read/write/subscription execution.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const status = await api.getOpcuaServerRoleStatus();
    const projection = status?.read_projection || {};
    return {
      profile: status?.profile,
      available: status?.available === true,
      executable: status?.executable === true,
      enabled: status?.enabled === true,
      running: status?.running === true,
      mode: status?.mode,
      blocker_code: status?.blocker_code,
      reason: status?.reason,
      listen_address: status?.listen_address,
      namespace_uri: status?.namespace_uri,
      mapping_count: status?.mapping_count,
      desired_revision: status?.desired_revision,
      applied_revision: status?.applied_revision,
      desired_equals_applied: status?.desired_equals_applied === true,
      runtime_matches_applied: status?.runtime_matches_applied === true,
      reconciled: status?.reconciled === true,
      listener_closed_applied: status?.listener_closed_applied === true,
      write_state_contract: status?.write_state_contract,
      warnings: Array.isArray(status?.warnings) ? status.warnings.slice(0, 64) : [],
      dependencies: (Array.isArray(status?.dependencies) ? status.dependencies : []).slice(0, 32).map((dependency) => ({
        module: dependency?.module,
        version: dependency?.version,
        license: dependency?.license,
        usage: dependency?.usage,
      })),
      read_projection: {
        available: projection.available === true,
        profile: projection.profile,
        scalar_types: Array.isArray(projection.scalar_types) ? projection.scalar_types.slice(0, 32) : [],
        max_request_bytes: projection.max_request_bytes,
        max_response_bytes: projection.max_response_bytes,
        max_scalar_bytes: projection.max_scalar_bytes,
        writes_enabled: projection.writes_enabled === true,
        network_listener_owned: projection.network_listener_owned === true,
      },
    };
  }, "OPCUA_SERVER_ROLE_STATUS_UNAVAILABLE"),
};

export const get_s7comm_server_config = {
  name: "get_s7comm_server_config",
  description:
    "Read the saved bounded s7comm-server-v1 endpoint revision, including disabled/enabled state, exact literal bind address and port profile, source-CIDR allowlist, called TSAP, PDU/client/rate/deadline bounds, and DB scalar mappings by portable normal-tag path. This tool is read only. It does not preview, confirm, save, enable, restart, or distribute the endpoint. Saved configuration is never proof of an applied or listening revision, and cloud configuration is not signed remote edge rollout.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const config = await api.getS7CommServerConfig();
    const blocks = Array.isArray(config?.data_blocks) ? config.data_blocks : [];
    const mappingCount = blocks.reduce(
      (sum, block) => sum + (Array.isArray(block?.fields) ? block.fields.length : 0),
      0
    );
    return {
      revision: config?.revision,
      digest: config?.digest,
      enabled: config?.enabled === true,
      bind_address: config?.bind_address,
      port: config?.port,
      bench_port: config?.bench_port === true,
      source_cidrs: Array.isArray(config?.source_cidrs) ? config.source_cidrs.slice(0, 64) : [],
      called_tsap: config?.called_tsap,
      pdu_cap: config?.pdu_cap,
      max_clients: config?.max_clients,
      max_items: config?.max_items,
      accept_rate_per_second: config?.accept_rate_per_second,
      accept_burst: config?.accept_burst,
      request_rate_per_second: config?.request_rate_per_second,
      request_burst: config?.request_burst,
      write_rate_per_second: config?.write_rate_per_second,
      write_burst: config?.write_burst,
      handshake_timeout_ms: config?.handshake_timeout_ms,
      setup_timeout_ms: config?.setup_timeout_ms,
      progress_timeout_ms: config?.progress_timeout_ms,
      idle_timeout_ms: config?.idle_timeout_ms,
      write_timeout_ms: config?.write_timeout_ms,
      drain_timeout_ms: config?.drain_timeout_ms,
      data_block_count: blocks.length,
      mapping_count: mappingCount,
      data_blocks_truncated: blocks.length > 20,
      data_blocks: blocks.slice(0, 20).map((block) => {
        const fields = Array.isArray(block?.fields) ? block.fields : [];
        return {
          number: block?.number,
          size: block?.size,
          description: block?.description,
          field_count: fields.length,
          fields_truncated: fields.length > 50,
          fields: fields.slice(0, 50).map((field) => ({
            byte_offset: field?.byte_offset,
            bit_offset: field?.bit_offset,
            type: field?.type,
            writable: field?.writable === true,
            tag_path: field?.tag_path,
            tag_type: field?.tag_type,
          })),
        };
      }),
    };
  }, "S7COMM_SERVER_CONFIG_UNAVAILABLE"),
};

export const get_s7comm_server_status = {
  name: "get_s7comm_server_status",
  description:
    "Read canonical s7comm-server-v1 target/runtime truth: local executable versus cloud configuration-only mode, desired and applied revisions/digests plus public integrity profile/sealed state, listener lifecycle and exact endpoint, fixed return/write-stage counters, structured stale/non_good/conversion quality-cause counters, bounded public-tag-path quality reasons, rates/timestamps, warnings, and the accepted-command-versus-observed-state contract. No diagnostic contains tag IDs, raw values, or a binding-key fingerprint. legacy-path-v0 is unsealed and rollback-ineligible; normal reviewed Save appends keyed-binding-v1. A binding-key mismatch means Retry or restore the original server secret, never destructive repair. Use reconciled for overall truth: desired_equals_applied proves only durable pointer equality, while runtime_matches_applied proves the executable runtime identity/listener state matches the applied revision. Lifecycle degraded means the latest audit-sink failure is newer than the latest recorded audit completion; the listener remains active while affected writes fail closed. Never infer a listener from saved config. The profile is DB-scalar compatibility only, has no Siemens identity/SZL or application authentication/encryption. Structured quality causes distinguish communication freshness loss from other bad quality without parsing diagnostic strings. Actor-attributed proposal/preview/confirmation/apply/reject and enable/disable configuration lifecycle audit is implemented; the operations-audit gate remains pending for per-event network rate-limit/audit-sink operations and broader operational evidence. Security, independent interoperability, HIL/soak, write-audit ambiguity recovery fault injection, packaging, SBOM, and signed remote rollout also remain gated. A write audit is reserved durably before dispatch and protocol success is returned only after accepted completion is recorded. Completion is not atomic with an external connector command: if that post-dispatch record fails, the wire receives failure while the reservation and possibly accepted command remain pending/ambiguous. Only repair_required=true classifies durable digest/schema/content corruption and exposes the reviewed Gateway GUI's authenticated audited fail-closed repair; transient network/database/auth failures offer Retry only. Repair stops S7Comm, appends a new empty disabled desired/applied revision, and preserves corrupt rows as evidence. This read-only AI tool cannot invoke it.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => redactPrivateTagIdentities(await api.getS7CommServerStatus()), "S7COMM_SERVER_STATUS_UNAVAILABLE"),
};

export const get_s7comm_server_revisions = {
  name: "get_s7comm_server_revisions",
  description:
    "Read up to 100 bounded public S7Comm revision summaries: revision/digest, endpoint, DB/mapping/writable counts, timestamps, rollback origin, integrity profile/seal, desired/applied flags, server-computed rollback eligibility, and its closed blocker. It never returns complete historical mappings or internal tag IDs. This tool cannot create a rollback preview or mutate history. Rollback is a reviewed Gateway-only workflow: the human reviews the exact semantic diff, current base revision, rollback-confirmation digest, warnings, and separately reported no-authentication acknowledgement requirement; success appends a higher revision and never rewrites history.",
  parameters: {
    type: "object",
    properties: {
      limit: { type: "integer", minimum: 1, maximum: 100, description: "Newest revision summaries to return (default 50)." },
    },
    additionalProperties: false,
  },
  execute: wrap(async ({ limit = 50 } = {}) => {
    const boundedLimit = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 50)));
    const payload = await api.getS7CommServerRevisions(boundedLimit);
    const revisions = Array.isArray(payload?.revisions) ? payload.revisions : [];
    return {
      profile: payload?.profile,
      revisions: revisions.slice(0, boundedLimit).map((revision) => ({
        revision: revision?.revision,
        digest: revision?.digest,
        enabled: revision?.enabled === true,
        bind_address: revision?.bind_address,
        port: revision?.port,
        called_tsap: revision?.called_tsap,
        data_block_count: revision?.data_block_count,
        mapping_count: revision?.mapping_count,
        writable_mapping_count: revision?.writable_mapping_count,
        rollback_of: revision?.rollback_of,
        created_at: revision?.created_at,
        applied_at: revision?.applied_at,
        integrity_profile: revision?.integrity_profile,
        sealed: revision?.sealed === true,
        desired: revision?.desired === true,
        applied: revision?.applied === true,
        rollback_eligible: revision?.rollback_eligible === true,
        rollback_blocker: revision?.rollback_blocker,
      })),
    };
  }, "S7COMM_SERVER_REVISIONS_UNAVAILABLE"),
};

export const get_s7comm_server_config_audit = {
  name: "get_s7comm_server_config_audit",
  description:
    "Read up to 100 newest actor-attributed S7Comm configuration-operation summaries. One bounded record follows a reviewed proposal through preview, exact confirmation, desired-only or local apply, rejection, apply failure, or expiry; enable and disable are explicit actions. The API uses the same read permission as S7Comm status/revision history. It never returns the live proposal identity, configuration body, tag identities or values, secrets, or binding-key fingerprint. This tool is read only and cannot preview, confirm, reject, enable, disable, save, restart, rollback, or repair. Per-event unauthenticated network rate-limit/audit-sink actions deliberately remain aggregate diagnostics rather than audit rows.",
  parameters: {
    type: "object",
    properties: {
      limit: { type: "integer", minimum: 1, maximum: 100, description: "Newest configuration operations to return (default 50)." },
    },
    additionalProperties: false,
  },
  execute: wrap(async ({ limit = 50 } = {}) => {
    const boundedLimit = Math.max(1, Math.min(100, Math.trunc(Number(limit) || 50)));
    const payload = await api.getS7CommServerConfigAudit(boundedLimit);
    const operations = Array.isArray(payload?.operations) ? payload.operations : [];
    return {
      profile: payload?.profile,
      operations: operations.slice(0, boundedLimit).map((operation) => ({
        actor_id: operation?.actor_id,
        actor_name: operation?.actor_name || operation?.actor_id,
        action: operation?.action,
        outcome: operation?.outcome,
        requested_enabled: operation?.requested_enabled === true,
        prior_enabled: operation?.prior_enabled,
        base_revision: operation?.base_revision,
        candidate_digest: operation?.candidate_digest,
        revision: operation?.revision,
        confirmation_required: operation?.confirmation_required === true,
        no_authentication_acknowledged: operation?.no_authentication_acknowledged === true,
        data_block_count: operation?.data_block_count,
        mapping_count: operation?.mapping_count,
        semantic_change_count: operation?.semantic_change_count,
        failure_stage: operation?.failure_stage,
        failure_code: operation?.failure_code,
        proposed_at: operation?.proposed_at,
        previewed_at: operation?.previewed_at,
        confirmed_at: operation?.confirmed_at,
        completed_at: operation?.completed_at,
        expires_at: operation?.expires_at,
      })),
    };
  }, "S7COMM_SERVER_CONFIG_AUDIT_UNAVAILABLE"),
};

export const list_opcua_servers = {
  name: "list_opcua_servers",
  description:
    "List configured OPC UA client/source connections without passwords. This does not list or imply an OPC UA server capability.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: wrap(async () => {
    const rows = await api.listOpcuaServers();
    return summarize(rows, [
      "id",
      "name",
      "endpoint_url",
      "security_mode",
      "security_policy",
      "auth_mode",
      "username",
      "has_password",
      "enabled",
    ]);
  }),
};

export const get_opcua_server_state = {
  name: "get_opcua_server_state",
  description:
    "Read live connection/subscription state for one configured OPC UA client connection. Use an id returned by list_opcua_servers; never guess it.",
  parameters: {
    type: "object",
    properties: { id: { type: "string", minLength: 1 } },
    required: ["id"],
    additionalProperties: false,
  },
  execute: wrap(({ id }) => api.getOpcuaServerState(id)),
};

// eslint-disable-next-line import/no-anonymous-default-export -- explicit name
const readOnlyTools = [
  list_datasources,
  describe_datasource,
  ping_datasource,
  list_db_schemas,
  list_db_tables,
  list_db_columns,
  get_supported_connector_types,
  list_api_profiles,
  list_profile_tags,
  list_credentials,
  test_credential,
  list_oauth_providers,
  list_oauth_apps,
  get_oauth_app,
  list_mqtt_brokers,
  get_mqtt_broker_state,
  list_mqtt_subscriptions,
  list_historian_providers,
  test_historian_provider,
  list_users,
  list_groups,
  get_group,
  list_invites,
  list_projects,
  list_tags,
  list_collectors,
  list_alarms,
  list_alarm_users,
  get_me,
  get_overview_stats,
  get_workspace_settings,
  list_ai_providers,
  get_ai_preference,
  list_edge_runtimes,
  get_edge_plc_programs,
  list_edge_gpio_bindings,
  get_edge_connectors,
  list_device_templates,
  get_runtime_hardware,
  inspect_edge_runtime_tags,
  list_plc_fleet_candidates,
  get_plc_deployment_signing_policy,
  list_plc_fleet_rollouts,
  get_plc_fleet_rollout,
  get_plc_fleet_rollout_events,
  get_plc_capabilities,
  list_plc_controllers,
  get_plc_controller_events,
  get_plc_controller_output_delivery,
  get_plc_controller_support,
  get_plc_fieldbus_readiness,
  get_modbus_runtime_status,
  list_modbus_devices,
  list_modbus_serial_ports,
  get_modbus_server_config,
  get_modbus_server_status,
  get_opcua_server_role_config,
  get_opcua_server_role_status,
  get_s7comm_server_config,
  get_s7comm_server_status,
  get_s7comm_server_revisions,
  get_s7comm_server_config_audit,
  list_opcua_servers,
  get_opcua_server_state,
  list_known_models,
  get_license,
  get_system_info,
  get_tls_config,
  check_for_updates,
];

export { readOnlyTools as default };
